import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, Users, Swords, ArrowRight, Loader } from "lucide-react";

/**
 * Public shareable Battle Card — no auth required.
 * URL: /card/battle/:id
 *
 * Rendered so it looks striking as a preview link in Discord / Twitter.
 * Sized to fit a 1200×630 open-graph screenshot naturally.
 */
export default function BattleCardPage() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/cards/battle/${id}`)
      .then(({ data }) => setCard(data))
      .catch((e) => setError(e?.response?.data?.detail || "That report is buried too deep."));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-bone">
        <div className="text-center space-y-4">
          <Skull className="w-8 h-8 mx-auto text-blood" />
          <p className="font-serif-h text-2xl">{error}</p>
          <Link to="/" className="btn-ghost inline-block" data-testid="card-fallback-home">
            THE FIELD IS THIS WAY
          </Link>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader className="w-6 h-6 text-blood animate-spin" />
      </div>
    );
  }

  const won = !!card.won;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-10">
      <div className="max-w-3xl w-full" data-testid="battle-card-page">
        {/* THE CARD */}
        <article
          className="relative border border-blood/40 bg-[#0a0303] p-10 lg:p-14 shadow-[0_0_60px_rgba(106,0,0,0.35)]"
          data-testid="battle-card"
        >
          {/* grain */}
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "url(data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22200%22 height=%22200%22 filter=%22url(%23n)%22/></svg>)" }} />

          <p className="font-mono text-[0.65rem] tracking-[0.4em] text-blood mb-4 relative">
            — FIELD REPORT · {card.region?.toUpperCase() || "—"} —
          </p>
          <h1 className="font-serif-h text-4xl lg:text-6xl text-bone leading-tight relative"
              data-testid="card-battle-name">
            {(card.battle_name || "The Field").toUpperCase()}
          </h1>

          <p className={`font-serif-h text-3xl lg:text-4xl mt-6 tracking-wider relative ${
            won ? "text-[#d5c9b1]" : "text-blood"
          }`} data-testid="card-outcome">
            {won ? "VICTORY" : "DEFEAT"}
          </p>

          <p className="text-lg lg:text-xl italic text-bone/85 mt-6 leading-relaxed relative"
             data-testid="card-quote">
            "{card.quote}"
          </p>

          <div className="grid grid-cols-3 gap-6 mt-10 border-t border-[var(--border-subtle)] pt-6 relative">
            <Metric icon={<Users className="w-3 h-3" />} label="COMMITTED" value={card.committed} />
            <Metric icon={<Skull className="w-3 h-3" />} label="FALLEN" value={card.zombie_casualties} tone="blood" />
            <Metric icon={<Swords className="w-3 h-3" />} label="CONVERTED" value={card.converted || card.human_kills} tone="bone" />
          </div>

          <div className="flex items-center justify-between mt-10 relative">
            <div>
              <p className="font-mono text-[0.6rem] tracking-[0.3em] text-muted-h">COMMANDED BY</p>
              <p className="font-serif-h text-2xl text-bone" data-testid="card-callsign">{card.callsign}</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[0.6rem] tracking-[0.3em] text-muted-h">ZOMBIE USURPER</p>
              <p className="font-mono text-[0.65rem] text-blood tracking-widest">zombieusurper.online</p>
            </div>
          </div>
        </article>

        <div className="mt-8 text-center">
          <Link to="/" className="btn-blood inline-flex items-center gap-2"
                data-testid="card-cta-join">
            JOIN THE HORDE <ArrowRight className="w-3 h-3" />
          </Link>
          <p className="text-[0.65rem] text-muted-h mt-3 tracking-widest font-mono">
            EVERY WEEK. RANK UP. OR ROT.
          </p>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon, label, value, tone }) {
  const color = tone === "blood" ? "text-blood" : tone === "bone" ? "text-[#d5c9b1]" : "text-bone";
  return (
    <div className="border-l-2 border-blood/30 pl-3">
      <p className="text-[0.55rem] tracking-[0.2em] font-mono text-muted-h flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`text-3xl tabular-nums font-serif-h mt-1 ${color}`}>{value ?? 0}</p>
    </div>
  );
}
